import { format, isToday, isTomorrow } from 'date-fns';
import { router } from 'expo-router';
import { useCallback, useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconChevronRight, IconTime, InlineAlert, Skeleton, Text, tokens } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { OPEN_PICKUP_TIME_MODAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import {
  getFireOrderInSecondsForPickupTime,
  resolveSchedulePickupTime,
} from './action-pickup-time-selector/earliest-pickup-time';
import { shouldShowAsapOnCheckoutSummary } from './action-pickup-time-selector/should-show-asap-pickup';
import { usePickupTimeOptions } from './pickup-time-options-context';

export default function PickupTimeSection() {
  const { setFireOrderIn } = useOrderContext();
  const {
    datePickerData,
    isRestaurantOpen,
    earliestPickupTimeInSeconds,
    todayStoreOpensAt,
    displayPickupTime,
    defaultPickupTime,
    allowAsapOnCheckoutSummary,
    displayRefreshTick,
    isLoadingData,
  } = usePickupTimeOptions();
  const asapThresholdMinutes = useFlag('enable-asap-pickup-threshold', 0);

  const schedulePickupTime = useMemo(
    () =>
      resolveSchedulePickupTime({
        displayPickupTime,
        defaultPickupTime,
        datePickerData,
      }),
    [displayPickupTime, defaultPickupTime, datePickerData]
  );

  const pickupDate = useMemo(
    () => schedulePickupTime ?? displayPickupTime ?? new Date(),
    [schedulePickupTime, displayPickupTime]
  );

  useEffect(() => {
    if (!schedulePickupTime) {
      setFireOrderIn(0);
      return;
    }

    setFireOrderIn(getFireOrderInSecondsForPickupTime(schedulePickupTime));
  }, [schedulePickupTime, setFireOrderIn]);

  const formatOrderTime = useCallback(
    (orderTime: Date) => {
      if (
        shouldShowAsapOnCheckoutSummary(
          orderTime,
          asapThresholdMinutes,
          earliestPickupTimeInSeconds,
          allowAsapOnCheckoutSummary,
          { storeOpensAt: todayStoreOpensAt }
        )
      ) {
        return 'ASAP';
      }

      const time = format(orderTime, 'h:mm a');

      if (!datePickerData?.length) {
        return isToday(orderTime) ? `Today - ${time}` : format(orderTime, 'PPp');
      }

      if (isToday(orderTime)) {
        return `Today - ${time}`;
      }
      if (isTomorrow(orderTime)) {
        return `Tomorrow - ${time}`;
      }
      return `${format(orderTime, 'eee')} - ${time}`;
    },
    [
      datePickerData,
      asapThresholdMinutes,
      earliestPickupTimeInSeconds,
      allowAsapOnCheckoutSummary,
      todayStoreOpensAt,
    ]
  );

  const pickupTimeLabel = useMemo(() => {
    void displayRefreshTick;
    return formatOrderTime(pickupDate);
  }, [pickupDate, formatOrderTime, displayRefreshTick]);

  if (isLoadingData) {
    return (
      <>
        <View
          style={[styles.section, styles.sectionBorder]}
          accessibilityLabel="Pick up time, loading"
        >
          <View style={styles.iconCircle}>
            <IconTime size={24} />
          </View>

          <View style={styles.flex1}>
            <Text.Ui size="sm">Pick up time:</Text.Ui>
            <Skeleton style={styles.timeSkeleton} />
          </View>

          <View style={styles.timeAndArrow}>
            <IconChevronRight size={24} />
          </View>
        </View>
        {!isRestaurantOpen && (
          <View style={styles.alertContainer}>
            <InlineAlert
              type="warning"
              message="Planning to place an order for later? Make sure this date and time works for you!"
            />
          </View>
        )}
      </>
    );
  }

  return (
    <>
      <Pressable
        style={[styles.linkSection, styles.sectionBorder]}
        onPress={() => router.setParams({ action: 'time' })}
        testID={OPEN_PICKUP_TIME_MODAL}
      >
        {({ pressed }) => (
          <View style={styles.section}>
            <View style={[styles.iconCircle, pressed && styles.iconCirclePressed]}>
              <IconTime size={24} />
            </View>

            <View style={styles.flex1}>
              <Text.Ui size="sm">Pick up time:</Text.Ui>
              <Text.Ui
                style={{
                  marginBottom: 4,
                }}
                size="md"
                weight="bold"
              >
                {pickupTimeLabel}
              </Text.Ui>
            </View>

            <View style={styles.timeAndArrow}>
              <IconChevronRight size={24} />
            </View>
          </View>
        )}
      </Pressable>
      {!isRestaurantOpen && (
        <View style={styles.alertContainer}>
          <InlineAlert
            type="warning"
            message="Planning to place an order for later? Make sure this date and time works for you!"
          />
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  linkSection: {
    width: '100%',
  },
  flex1: { flex: 1 },
  timeAndArrow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },

  section: {
    width: '100%',
    gap: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionBorder: {
    width: '100%',
    borderBottomColor: tokens.colors.$black10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },

  iconCirclePressed: {
    backgroundColor: tokens.colors.$black10,
  },
  iconCircle: {
    height: 40,
    width: 40,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.$houseLight,
  },
  timeSkeleton: {
    height: 18,
    width: 120,
    marginTop: 4,
    marginBottom: 4,
  },
  alertContainer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    borderBottomColor: tokens.colors.$black10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
});
